/**
 * <AdminSettingsTab />
 *
 * Form that reads + writes `/api/admin/settings`. Also exposes a one-shot
 * "send test email" button so the admin can validate their SMTP creds
 * without leaving the tab.
 *
 * Design notes:
 *   * `smtp_password` is never read back from the server (the public
 *     projection only reveals a boolean).  We leave the password field
 *     blank on load -- empty submission means "don't change"; typing
 *     something means "update with this".
 *   * Every boolean toggle is wired as a checkbox, not a floating switch,
 *     to match the senior-friendly design system (no ambiguous UI).
 */
import * as React from "react";
import { AlertCircle, CheckCircle2, Database, Image, Mail, Send, Trash2, Type } from "lucide-react";
import { api, extractApiError, parseApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { FONT_OPTIONS, applyFont, resolveFont } from "../../../lib/fonts";
import { usePublicSettings } from "../../../contexts/PublicSettingsContext";
import { useLogoContext } from "../../../contexts/LogoContext";

interface SettingsPayload {
  smtp_host: string | null;
  smtp_port: number;
  smtp_user: string | null;
  smtp_password_set: boolean;
  smtp_use_tls: boolean;
  smtp_use_ssl: boolean;
  sender_email: string | null;
  sender_name: string;
  emails_enabled: boolean;
  notify_on_signup: boolean;
  notify_on_order: boolean;
  notify_on_forum_reply: boolean;
  marketplace_open: boolean;
  forum_open: boolean;
  allow_dealer_signup: boolean;
  dealer_subscription_price_usd: number;
  dealer_commission_pct: number;
  participant_commission_pct: number;
  ui_font_family: string;
  updated_at: string;
}

export function AdminSettingsTab() {
  const { refresh: refreshPublicSettings } = usePublicSettings();
  const [loaded, setLoaded] = React.useState<SettingsPayload | null>(null);
  const [form, setForm] = React.useState<any>(null);
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

  const [testEmail, setTestEmail] = React.useState("");
  const [testBusy, setTestBusy] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{ ok: boolean; msg: string } | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get<SettingsPayload>("/admin/settings");
        setLoaded(r.data);
        setForm({
          smtp_host: r.data.smtp_host ?? "",
          smtp_port: r.data.smtp_port,
          smtp_user: r.data.smtp_user ?? "",
          smtp_password: "",
          smtp_use_tls: r.data.smtp_use_tls,
          smtp_use_ssl: r.data.smtp_use_ssl,
          sender_email: r.data.sender_email ?? "",
          sender_name: r.data.sender_name ?? "Parts Buoy",
          emails_enabled: r.data.emails_enabled,
          notify_on_signup: r.data.notify_on_signup,
          notify_on_order: r.data.notify_on_order,
          notify_on_forum_reply: r.data.notify_on_forum_reply,
          marketplace_open: r.data.marketplace_open,
          forum_open: r.data.forum_open,
          allow_dealer_signup: r.data.allow_dealer_signup,
          dealer_subscription_price_usd: r.data.dealer_subscription_price_usd,
          dealer_commission_pct_display: Number((r.data.dealer_commission_pct * 100).toFixed(2)),
          participant_commission_pct_display: Number(
            (r.data.participant_commission_pct * 100).toFixed(2),
          ),
          ui_font_family: r.data.ui_font_family || "instrument_sans",
        });
      } catch (err) {
        setError(extractApiError(err));
      }
    })();
  }, []);

  const setField = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setSavedAt(null);
    try {
      const body: any = { ...form };
      // Blank password field = don't touch it. Strip so server leaves it.
      if (!body.smtp_password) delete body.smtp_password;
      // Blank sender email = don't touch it either. The form always submits
      // every section (SMTP, notifications, commerce, ...) in one payload,
      // so an admin saving only the Commerce card would otherwise send
      // sender_email: "" and trip EmailStr validation on a field they never
      // touched. Same "blank = keep existing" contract the backend now
      // enforces for this field.
      if (!body.sender_email) delete body.sender_email;
      body.smtp_port = Number(body.smtp_port) || 587;

      // Commerce fields — server stores commission as 0..1 decimal.
      // Our form exposes percent values for humans; convert here.
      if (body.dealer_commission_pct_display !== undefined) {
        body.dealer_commission_pct =
          Number(body.dealer_commission_pct_display) / 100;
        delete body.dealer_commission_pct_display;
      }
      if (body.participant_commission_pct_display !== undefined) {
        body.participant_commission_pct =
          Number(body.participant_commission_pct_display) / 100;
        delete body.participant_commission_pct_display;
      }
      if (body.dealer_subscription_price_usd !== undefined) {
        body.dealer_subscription_price_usd = Number(body.dealer_subscription_price_usd);
      }

      const r = await api.put<SettingsPayload>("/admin/settings", body);
      setLoaded(r.data);
      setSavedAt(r.data.updated_at);
      // Clear password field after save -- the server only stores a hash.
      setForm((f: any) => ({ ...f, smtp_password: "" }));
      // Re-apply the (possibly new) font immediately AND let every other
      // component pick it up via the PublicSettings context.
      applyFont(r.data.ui_font_family);
      await refreshPublicSettings();
    } catch (err) {
      // 422s carry per-field detail via the global RequestValidationError
      // handler (main.py) -- surface which field failed instead of a
      // generic toast so e.g. an invalid SMTP sender address doesn't read
      // as "something broke everywhere".
      const parsed = parseApiError(err);
      setFieldErrors(parsed.fieldErrors);
      setError(parsed.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      setTestResult({ ok: false, msg: "Enter a recipient email address first." });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    try {
      // Phase 14-A: response shape is {ok, sent, reason}.
      // `sent=true` ⇒ green message; `sent=false` ⇒ red with `reason`.
      const r = await api.post<{
        ok: boolean;
        sent: boolean;
        reason: string | null;
      }>("/admin/settings/test-email", { to_email: testEmail.trim() });
      const { sent, reason } = r.data;
      setTestResult({
        ok: !!sent,
        msg: sent
          ? "Email dispatched successfully."
          : reason ?? "Email was not sent.",
      });
    } catch (err) {
      setTestResult({ ok: false, msg: extractApiError(err) });
    } finally {
      setTestBusy(false);
    }
  };

  if (!form || !loaded) {
    return (
      <div className="py-12">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-6" data-testid="admin-settings-form">
      {/* SMTP credentials ------------------------------------------------ */}
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <Mail className="h-5 w-5" aria-hidden /> SMTP credentials
            </h2>
            <p className="text-ink-muted text-sm mt-1">
              These power every outbound email. Stored in MongoDB, not .env.
            </p>
          </div>
          {loaded.smtp_password_set ? (
            <Badge variant="brand">Password stored</Badge>
          ) : (
            <Badge variant="outline">No password</Badge>
          )}
        </CardHeader>
        <CardBody>
          {/* Phase 14-A status pill — drives off the live `loaded` state,
              which the save handler refreshes on PUT success. */}
          <EmailStatusPill
            enabled={loaded.emails_enabled}
            smtpHost={loaded.smtp_host}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SMTP host"
              value={form.smtp_host}
              onChange={(e) => setField("smtp_host", e.target.value)}
              placeholder="smtp.sendgrid.net"
              error={fieldErrors.smtp_host}
              data-testid="settings-smtp-host"
            />
            <Input
              label="SMTP port"
              type="number"
              value={form.smtp_port}
              onChange={(e) => setField("smtp_port", e.target.value)}
              placeholder="587"
              error={fieldErrors.smtp_port}
              data-testid="settings-smtp-port"
            />
            <Input
              label="SMTP username"
              value={form.smtp_user}
              onChange={(e) => setField("smtp_user", e.target.value)}
              placeholder="apikey"
              error={fieldErrors.smtp_user}
              data-testid="settings-smtp-user"
            />
            <Input
              label="SMTP password"
              type="password"
              value={form.smtp_password}
              onChange={(e) => setField("smtp_password", e.target.value)}
              placeholder={loaded.smtp_password_set ? "•••••••• (leave blank to keep)" : "Enter password"}
              hint="Blank = leave unchanged. Type to update."
              error={fieldErrors.smtp_password}
              data-testid="settings-smtp-password"
            />
            <Input
              label="Sender email (from)"
              value={form.sender_email}
              onChange={(e) => setField("sender_email", e.target.value)}
              placeholder="hello@partsbuoy.com"
              error={fieldErrors.sender_email}
              data-testid="settings-sender-email"
            />
            <Input
              label="Sender name"
              value={form.sender_name}
              onChange={(e) => setField("sender_name", e.target.value)}
              placeholder="Parts Buoy"
              data-testid="settings-sender-name"
            />
          </div>

          <div className="flex flex-wrap gap-4 mt-4">
            <BoolRow
              label="Use STARTTLS (port 587 / 25)"
              value={form.smtp_use_tls}
              onChange={(v) => setField("smtp_use_tls", v)}
              testid="settings-use-tls"
            />
            <BoolRow
              label="Use implicit SSL (port 465)"
              value={form.smtp_use_ssl}
              onChange={(v) => setField("smtp_use_ssl", v)}
              testid="settings-use-ssl"
            />
          </div>
        </CardBody>
      </Card>

      {/* Notification toggles ------------------------------------------- */}
      <Card elevated>
        <CardHeader>
          <h2 className="text-xl font-bold">Notifications</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          <BoolRow
            label="Emails enabled (master switch)"
            value={form.emails_enabled}
            onChange={(v) => setField("emails_enabled", v)}
            testid="settings-emails-enabled"
          />
          <BoolRow
            label="Signup welcome email"
            value={form.notify_on_signup}
            onChange={(v) => setField("notify_on_signup", v)}
            testid="settings-notify-signup"
          />
          <BoolRow
            label="Order confirmation email"
            value={form.notify_on_order}
            onChange={(v) => setField("notify_on_order", v)}
            testid="settings-notify-order"
          />
          <BoolRow
            label="Forum reply alert email"
            value={form.notify_on_forum_reply}
            onChange={(v) => setField("notify_on_forum_reply", v)}
            testid="settings-notify-reply"
          />
        </CardBody>
      </Card>

      {/* Platform toggles ----------------------------------------------- */}
      <Card elevated>
        <CardHeader>
          <h2 className="text-xl font-bold">Platform toggles</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          <BoolRow
            label="Marketplace open to buyers"
            value={form.marketplace_open}
            onChange={(v) => setField("marketplace_open", v)}
            testid="settings-marketplace-open"
          />
          <BoolRow
            label="Forum open for new threads"
            value={form.forum_open}
            onChange={(v) => setField("forum_open", v)}
            testid="settings-forum-open"
          />
          <BoolRow
            label="Allow dealer self-signup"
            value={form.allow_dealer_signup}
            onChange={(v) => setField("allow_dealer_signup", v)}
            testid="settings-dealer-signup"
          />
        </CardBody>
      </Card>

      {/* Commerce (fees + subscription price) --------------------------- */}
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold">Commerce</h2>
            <p className="text-ink-muted text-sm mt-1">
              Tune the monthly Dealer subscription price and platform
              commission percentages. New orders pick up the new rate
              immediately; existing orders keep the rate captured at purchase.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Dealer subscription ($/mo)"
              type="number"
              min="0"
              step="0.50"
              value={form.dealer_subscription_price_usd}
              onChange={(e) => setField("dealer_subscription_price_usd", e.target.value)}
              hint="Charged monthly via Stripe."
              error={fieldErrors.dealer_subscription_price_usd}
              data-testid="settings-dealer-price"
            />
            <Input
              label="Dealer commission %"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.dealer_commission_pct_display}
              onChange={(e) => setField("dealer_commission_pct_display", e.target.value)}
              hint="Percentage of each order total."
              error={fieldErrors.dealer_commission_pct}
              data-testid="settings-dealer-commission"
            />
            <Input
              label="Participant commission %"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.participant_commission_pct_display}
              onChange={(e) => setField("participant_commission_pct_display", e.target.value)}
              hint="Percentage of each order total."
              error={fieldErrors.participant_commission_pct}
              data-testid="settings-participant-commission"
            />
          </div>
        </CardBody>
      </Card>

      {/* Appearance (typography) ---------------------------------------- */}
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <Type className="h-5 w-5" aria-hidden /> Appearance
            </h2>
            <p className="text-ink-muted text-sm mt-1">
              Pick the typeface used across the whole marketplace. Applies
              everywhere the moment you hit Save — no reload needed.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,320px)_1fr] gap-4 items-start">
            <Select
              label="UI font family"
              value={form.ui_font_family}
              onChange={(e) => {
                const next = e.target.value;
                setField("ui_font_family", next);
                // Live preview in this tab before saving.
                applyFont(next);
              }}
              options={Object.values(FONT_OPTIONS).map((f) => ({
                value: f.key,
                label: f.label,
              }))}
              hint={resolveFont(form.ui_font_family).note}
              data-testid="settings-ui-font"
            />
            <div
              className="rounded-xl border-2 border-surface-border bg-surface-sunken px-4 py-3"
              data-testid="settings-font-preview"
              style={{ fontFamily: resolveFont(form.ui_font_family).stack }}
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">
                Live preview
              </div>
              <div className="text-2xl font-bold text-ink">
                The quick brown fox jumps over the lazy dog.
              </div>
              <div className="text-sm text-ink mt-1">
                Parts Buoy · 1234567890 · info@partsbuoy.com
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Site Logo ------------------------------------------------------ */}
      <SiteLogoSection />

      {/* Actions -------------------------------------------------------- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="text-sm">
          {savedAt && (
            <span
              className="inline-flex items-center gap-1.5 text-ink font-semibold"
              data-testid="settings-saved"
            >
              <CheckCircle2 className="h-4 w-4 text-brand" /> Saved
            </span>
          )}
          {error && (
            <span
              data-testid="settings-error"
              className="inline-flex items-center gap-1.5 text-brand font-semibold"
            >
              <AlertCircle className="h-4 w-4" /> {error}
            </span>
          )}
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={saving}
          data-testid="settings-save-btn"
        >
          Save settings
        </Button>
      </div>

      {/* Test email ----------------------------------------------------- */}
      <Card className="border-2 border-ink">
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold">Send a test email</h2>
            <p className="text-ink-muted text-sm mt-1">
              Uses the saved credentials above. Requires the master switch to be on.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <Input
                label="Send to"
                hideLabel
                type="email"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="settings-test-email-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={sendTest}
              loading={testBusy}
              leadingIcon={<Send className="h-4 w-4" />}
              data-testid="settings-test-email-btn"
            >
              Send test
            </Button>
          </div>
          {testResult && (
            <p
              data-testid="settings-test-email-result"
              className={
                testResult.ok
                  ? "mt-3 text-sm font-semibold text-ink"
                  : "mt-3 text-sm font-semibold text-brand"
              }
            >
              {testResult.ok ? "Sent:" : "Not sent:"} {testResult.msg}
            </p>
          )}
        </CardBody>
      </Card>

      {/* Demo data -------------------------------------------------------- */}
      <DemoDataSection />
    </form>
  );
}

/**
 * <SiteLogoSection />
 *
 * Lets the admin upload a new logo from the Settings tab.  Upload flow:
 *   1. User picks a file → local object URL previewed immediately, no upload yet.
 *   2. "Save logo" button POSTs to /api/admin/settings/logo.
 *   3. On success: previews update to server-returned URLs, LogoContext is
 *      refreshed so the site header picks up the new logo instantly.
 *
 * The favicon and product-placeholder previews shown before save are scaled
 * from the local object URL (the server hasn't processed them yet).
 */
function SiteLogoSection() {
  const { logoUrl, faviconUrl, productPlaceholderUrl, refreshLogo } = useLogoContext();
  const fileRef = React.useRef<HTMLInputElement>(null);

  const [localPreview, setLocalPreview] = React.useState<string | null>(null);
  const [localFileName, setLocalFileName] = React.useState<string | null>(null);
  const [localFileSize, setLocalFileSize] = React.useState<number | null>(null);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);

  const [saving, setSaving] = React.useState(false);
  const [saveResult, setSaveResult] = React.useState<{ ok: boolean; msg: string } | null>(null);

  // After a successful save we display the server-returned URLs.
  const [savedUrls, setSavedUrls] = React.useState<{
    logo_url: string;
    favicon_url: string;
    og_image_url: string;
    product_placeholder_url: string;
  } | null>(null);

  const currentLogoUrl = savedUrls?.logo_url ?? logoUrl;
  const currentFaviconUrl = savedUrls?.favicon_url ?? faviconUrl;
  const currentPlaceholderUrl = savedUrls?.product_placeholder_url ?? productPlaceholderUrl;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (localPreview) URL.revokeObjectURL(localPreview);
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    setLocalFileName(file.name);
    setLocalFileSize(file.size);
    setPendingFile(file);
    setSaveResult(null);
    setSavedUrls(null);
  };

  const saveLogo = async () => {
    if (!pendingFile) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      const r = await api.post<{
        logo_url: string;
        favicon_url: string;
        og_image_url: string;
        product_placeholder_url: string;
      }>("/admin/settings/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSavedUrls(r.data);
      setSaveResult({ ok: true, msg: "Logo updated." });
      setLocalPreview(null);
      setPendingFile(null);
      setLocalFileName(null);
      setLocalFileSize(null);
      // Propagate the new logo to the site-wide header and product cards.
      await refreshLogo();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ?? extractApiError(err) ?? "Upload failed.";
      setSaveResult({ ok: false, msg: String(msg) });
    } finally {
      setSaving(false);
    }
  };

  const displayLogoUrl = localPreview ?? currentLogoUrl;
  const displayFaviconUrl = localPreview ?? currentFaviconUrl;
  const displayPlaceholderUrl = localPreview ?? currentPlaceholderUrl;
  const hasUnsavedFile = !!pendingFile;

  return (
    <Card elevated>
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <Image className="h-5 w-5" aria-hidden /> Site Logo
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Upload a new logo. The favicon, OG share image, and product
            placeholder are generated automatically from the same file.
          </p>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-5">
          {/* Current / preview logo */}
          <div>
            <p className="text-sm font-semibold text-ink-muted mb-2">
              {localPreview ? "Preview (not yet saved)" : "Current logo"}
            </p>
            <div className="inline-flex items-center justify-center h-32 w-32 rounded-xl border-2 border-surface-border bg-surface-sunken overflow-hidden">
              <img
                src={displayLogoUrl}
                alt="Logo preview"
                className="max-h-full max-w-full object-contain p-2"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = "/logo.png";
                }}
              />
            </div>
            {localPreview && localFileName && localFileSize !== null && (
              <p className="mt-1 text-xs text-ink-muted">
                {localFileName} ({(localFileSize / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {/* Upload button */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={onFileChange}
              data-testid="logo-file-input"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => fileRef.current?.click()}
              data-testid="logo-upload-btn"
            >
              Upload new logo
            </Button>
            <p className="mt-1 text-xs text-ink-muted">PNG, JPG, or WEBP. Max 8 MB.</p>
          </div>

          {/* Derived image previews */}
          <div className="flex flex-wrap gap-6 items-end">
            <div>
              <p className="text-xs font-semibold text-ink-muted mb-1">
                Favicon preview (32×32)
              </p>
              <div className="h-8 w-8 rounded border border-surface-border bg-surface-sunken overflow-hidden flex items-center justify-center">
                <img
                  src={displayFaviconUrl}
                  alt="Favicon preview"
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = "/favicon.png";
                  }}
                />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-muted mb-1">
                Product placeholder preview
              </p>
              <div className="h-16 w-24 rounded border border-surface-border bg-surface-sunken overflow-hidden flex items-center justify-center">
                <img
                  src={displayPlaceholderUrl}
                  alt="Product placeholder preview"
                  className="max-h-full max-w-full object-contain p-1"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = "/logo.png";
                  }}
                />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-muted mb-1">
                OG image preview (scaled)
              </p>
              <div
                className="h-16 rounded border border-surface-border overflow-hidden flex items-center justify-center"
                style={{ width: 120, background: "#1a1a1a" }}
              >
                <img
                  src={localPreview ?? savedUrls?.og_image_url ?? logoUrl}
                  alt="OG image preview"
                  className="max-h-full max-w-full object-contain p-1"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = "/logo.png";
                  }}
                />
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="primary"
              size="md"
              loading={saving}
              disabled={!hasUnsavedFile}
              onClick={saveLogo}
              data-testid="logo-save-btn"
            >
              Save logo
            </Button>
            {saveResult && (
              <p
                className={
                  saveResult.ok
                    ? "text-sm font-semibold text-ink"
                    : "text-sm font-semibold text-brand"
                }
                data-testid="logo-save-result"
              >
                {saveResult.ok ? (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-brand" /> {saveResult.msg}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" /> {saveResult.msg}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}


/**
 * <EmailStatusPill />
 *
 * Phase 14-A traffic-light status indicator that sits at the top of the
 * SMTP card. Drives a three-state pill off `loaded.emails_enabled` and
 * `loaded.smtp_host`:
 *
 *   * OFF                       gray pill, no hint
 *   * ON + smtp_host blank      yellow pill + one-line "will be dropped" hint
 *   * ON + smtp_host present    green pill, no hint
 *
 * The yellow / green colours are a deliberate deviation from the strict
 * Red / Black / White brand palette because traffic-light affordance is
 * universally understood for status indicators. Marketing surfaces stay
 * on-palette; this pill is the one accepted exception.
 */
function EmailStatusPill({
  enabled,
  smtpHost,
}: {
  enabled: boolean;
  smtpHost: string | null | undefined;
}) {
  const configured = !!(smtpHost && smtpHost.trim().length > 0);

  let pillClass: string;
  let label: string;
  let hint: string | null = null;
  let testidState: "off" | "on-misconfigured" | "on-configured";

  if (!enabled) {
    pillClass =
      "bg-surface-sunken text-ink-muted border border-surface-border";
    label = "Email: OFF";
    testidState = "off";
  } else if (!configured) {
    pillClass =
      "bg-amber-100 text-amber-900 border border-amber-300";
    label = "Email: ON — SMTP not configured";
    hint =
      "Emails are enabled but will be dropped until SMTP is configured below.";
    testidState = "on-misconfigured";
  } else {
    pillClass =
      "bg-emerald-100 text-emerald-800 border border-emerald-300";
    label = "Email: ON — SMTP configured";
    testidState = "on-configured";
  }

  return (
    <div
      className="mb-4"
      data-testid="settings-email-status"
      data-state={testidState}
    >
      <span
        className={
          "inline-flex items-center font-semibold uppercase rounded-full text-[11px] px-2.5 py-1 tracking-wider " +
          pillClass
        }
        data-testid="settings-email-status-pill"
      >
        {label}
      </span>
      {hint && (
        <p
          className="mt-2 text-sm text-ink-muted"
          data-testid="settings-email-status-hint"
        >
          {hint}
        </p>
      )}
    </div>
  );
}


/** Inline labelled checkbox row. */
function BoolRow({
  label,
  value,
  onChange,
  testid,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  testid: string;
}) {
  return (
    <label
      className="inline-flex items-center gap-3 cursor-pointer select-none"
      data-testid={`${testid}-wrap`}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-6 w-6 accent-brand"
        data-testid={testid}
      />
      <span className="text-ink font-semibold">{label}</span>
    </label>
  );
}


/**
 * Admin-only section to populate or wipe demo data.  Kept in this file
 * because it's only ever rendered inside the Settings tab.
 */
function DemoDataSection() {
  const [seedBusy, setSeedBusy] = React.useState(false);
  const [wipeBusy, setWipeBusy] = React.useState(false);
  const [result, setResult] = React.useState<{ ok: boolean; msg: string } | null>(null);
  const [wipeConfirm, setWipeConfirm] = React.useState(false);

  const seed = async () => {
    setSeedBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/seed-demo");
      const c = r.data?.counts ?? {};
      const already = r.data?.already_seeded;
      setResult({
        ok: true,
        msg: already
          ? `Demo data already present (users=${c.users}, threads=${c.threads}, products=${c.products}).`
          : `Seeded: ${c.users} users · ${c.categories} categories · ${c.threads} threads · ${c.replies} replies · ${c.products} products.`,
      });
    } catch (err) {
      setResult({ ok: false, msg: extractApiError(err) });
    } finally {
      setSeedBusy(false);
    }
  };

  const wipe = async () => {
    if (!wipeConfirm) {
      setWipeConfirm(true);
      return;
    }
    setWipeBusy(true);
    setResult(null);
    try {
      const r = await api.delete("/admin/wipe-demo");
      const d = r.data?.deleted ?? {};
      const total = Object.values(d).reduce(
        (acc: number, v: any) => acc + Number(v || 0),
        0,
      );
      setResult({
        ok: true,
        msg: `Wiped ${total} demo documents (${Object.entries(d)
          .map(([k, v]) => `${k}=${v}`)
          .join(", ")}).`,
      });
      setWipeConfirm(false);
    } catch (err) {
      setResult({ ok: false, msg: extractApiError(err) });
    } finally {
      setWipeBusy(false);
    }
  };

  return (
    <Card className="border-2 border-brand" data-testid="demo-data-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <Database className="h-5 w-5 text-brand" aria-hidden /> Demo data
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Populate or wipe the sample dataset used for client demos. Only
            documents flagged <code className="font-mono text-xs">is_demo=true</code> are
            affected — live marketplace data is safe.
          </p>
        </div>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            loading={seedBusy}
            onClick={seed}
            leadingIcon={<Database className="h-5 w-5" />}
            data-testid="demo-seed-btn"
          >
            Populate demo data
          </Button>
          <Button
            type="button"
            variant={wipeConfirm ? "primary" : "outline"}
            size="lg"
            loading={wipeBusy}
            onClick={wipe}
            leadingIcon={<Trash2 className="h-5 w-5" />}
            data-testid="demo-wipe-btn"
          >
            {wipeConfirm ? "Click again to confirm wipe" : "Wipe demo data"}
          </Button>
        </div>
        {result && (
          <p
            data-testid="demo-result"
            className={
              result.ok
                ? "mt-3 text-sm font-semibold text-ink"
                : "mt-3 text-sm font-semibold text-brand"
            }
          >
            {result.msg}
          </p>
        )}
        <p className="mt-3 text-xs text-ink-muted">
          Demo accounts sign in with password{" "}
          <code className="font-mono">DemoPass#2026</code>.
        </p>
      </CardBody>
    </Card>
  );
}

